import { DescriptionItem, DescriptionList } from "@/components/display/DescriptionList";
import { ReviewModalItem } from "@/components/display/ReviewModalItem";
import { useGetCurrentUserQuery, useGetToolUsageQuery, useIsOwnerQuery } from "@/store/services/supabase";
import ArrowUpOutlined from "@ant-design/icons/es/icons/ArrowUpOutlined";
import { useDebouncedCallback } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { Alert, Button, Collapse, Modal, Progress, Statistic } from "antd";
import { default as classnames, default as classNames } from "classnames";
import Link from "next/link";
import { MutableRefObject, PropsWithChildren, ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { Components } from "react-markdown";
import styles from "./ScreeningLimitReview.module.scss";
export interface ScreeningUploadReviewProps extends PropsWithChildren {
  title: string;
  isOpen: boolean;
  continueText: string;
  onContinue: () => void;
  uploadedAssetCount: number;
  setRefs?: (refs: ScreeningReviewModalRefs) => void;
  onBack: () => void;
  basicSettings: DescriptionItem[];
  advancedSettings: DescriptionItem[];
}
export interface ScreeningReviewModalRefs {
  proceed: MutableRefObject<null>;
}
export const ScreeningStatsRow = ({
  title,
  children,
  className
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => <div className={classnames(styles.statsRowContainer, className)} data-sentry-component="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
    <div className={classNames(styles.statsTitle)}>{title}</div>
    {children && <div className={styles.statsValue}>{children}</div>}
  </div>;
export const ScreeningLimitReview = ({
  title,
  isOpen,
  continueText,
  onContinue,
  uploadedAssetCount,
  setRefs,
  onBack,
  children,
  basicSettings,
  advancedSettings
}: ScreeningUploadReviewProps) => {
  const {
    data: isOwner
  } = useIsOwnerQuery();
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights.uploadLimitAlert"
  });
  const {
    t: settings
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const proceedRef = useRef(null);
  const handleRefsOnOpen = useDebouncedCallback(() => {
    if (!setRefs) {
      return;
    }
    setRefs({
      proceed: proceedRef
    });
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (isOpen) handleRefsOnOpen();else proceedRef.current = null;
  }, [isOpen, proceedRef]);
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) return <></>;
  const currentUsage = toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.screening;
  if (!currentUsage || typeof currentUsage.quota !== "number" || currentUsage.quota === 0) {
    Sentry.captureException("portfolioExportUploadReview", {
      tags: {
        section: "Quota Limit Error"
      },
      extra: {
        userId: currentUser?.id,
        orgId: currentUser?.org_id,
        toolUsage,
        currentUsage: currentUsage
      }
    });
  }
  const uploadedAssetPercent = uploadedAssetCount / currentUsage.quota * 100;
  const futureUsage = currentUsage.usage + uploadedAssetCount;
  const futureUsagePercent = futureUsage / currentUsage.quota * 100;
  const showWarning = futureUsagePercent > 80 && futureUsagePercent <= 100;
  const showError = futureUsagePercent > 100;
  const currentUsageStat = `${futureUsage} / ${currentUsage.quota.toLocaleString()} assets (${futureUsagePercent.toFixed(2)}%)`;
  const usageAfterUploadStat = `${uploadedAssetCount} (${uploadedAssetPercent.toFixed(2)}%)`;
  const exceedsQuotaBuffer = parseFloat(futureUsagePercent.toFixed(2)) > currentUsage.hardLimitPercent;
  const components: Components = {
    a: props => {
      return <Link href={props.href || ""} target="_blank">
          {props.children}
        </Link>;
    }
  };
  return <Modal title={title} open={isOpen} onCancel={onBack} footer={null} width={560} wrapClassName={styles.reviewScreeningContainer} data-sentry-element="Modal" data-sentry-component="ScreeningLimitReview" data-sentry-source-file="ScreeningLimitReview.tsx">
      <div className="flex flex-col gap-4">
        <ReviewModalItem title="Review Upload" className={styles.reviewContainer} data-sentry-element="ReviewModalItem" data-sentry-source-file="ScreeningLimitReview.tsx">
          <div className={styles.statsContainer}>
            <DescriptionList verticalAlign="top" labelWidth={155} items={basicSettings} data-sentry-element="DescriptionList" data-sentry-source-file="ScreeningLimitReview.tsx" />
          </div>
        </ReviewModalItem>
        <Collapse size="small" classNames={{
        root: "border !border-subtle rounded-lg bg-surface  *:!border-subtle [&_.ant-collapse-content]:border-t-0 [&_.ant-collapse-panel]:!border-subtle",
        header: "border-b-0",
        body: "border-t-0"
      }} expandIconPlacement="start" items={[{
        label: settings("advancedAnalysisSettings"),
        children: <DescriptionList verticalAlign="top" labelWidth={155} items={advancedSettings} />
      }]} data-sentry-element="Collapse" data-sentry-source-file="ScreeningLimitReview.tsx" />

        {children}

        <ReviewModalItem title="Usage Confirmation" className={styles.reviewContainer} data-sentry-element="ReviewModalItem" data-sentry-source-file="ScreeningLimitReview.tsx">
          <ScreeningStatsRow title="Assets Used" className="!items-start" data-sentry-element="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
            <div className="flex w-full flex-row justify-between gap-1">
              <div className="flex flex-col gap-1">
                <Statistic value={currentUsageStat} className="*:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
                <Statistic value={usageAfterUploadStat} className="*:!text-sm *:!text-gray-400" suffix={<ArrowUpOutlined />} data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
              </div>
              <Progress type="circle" strokeWidth={16} size={42} percent={futureUsagePercent} success={{
              percent: currentUsage.usagePercent,
              strokeColor: "#cdcdcd"
            }} strokeColor={"var(--color-accent)"} className={`${styles.progressContainer} ${showError && styles.error}`} showInfo={false} style={{
              gridColumn: "2 / 4"
            }} data-sentry-element="Progress" data-sentry-source-file="ScreeningLimitReview.tsx" />
            </div>
          </ScreeningStatsRow>
        </ReviewModalItem>
      </div>
      {showWarning && <Alert message={<Markdown components={components}>{isOwner ? t("owner.warning") : t("user.warning")}</Markdown>} type="warning" showIcon className="mt-4" />}
      {showError && <Alert message={<div>
              <div>
                <strong>Limit Reached</strong>
              </div>
              <Markdown components={components}>{isOwner ? t("owner.error") : t("user.error")}</Markdown>
            </div>} type="error" showIcon className="mt-4" />}
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button type="text" onClick={onBack} data-sentry-element="Button" data-sentry-source-file="ScreeningLimitReview.tsx">
          Back
        </Button>
        <Button ref={proceedRef} disabled={currentUsage.alwaysEnabled ? false : currentUsage.enabled ? exceedsQuotaBuffer : true} onClick={onContinue} type="primary" data-sentry-element="Button" data-sentry-source-file="ScreeningLimitReview.tsx">
          {continueText}
        </Button>
      </div>
    </Modal>;
};